import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { FormProvider, useForm } from 'react-hook-form';
import { Flex, Text } from '@chakra-ui/react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCheckCircle,
  faFileXmark,
  faTriangleExclamation
} from '@fortawesome/pro-regular-svg-icons';
import { Button, TextArea } from '@morressier/alembic';
import { Layout } from 'src/components/Layout';
import { updateCoReviewDeclineReason } from 'src/api/mutations';
import { useConfirmNavigation } from 'src/hooks/useConfirmNavigation';
import useIsLoggedIn from 'src/hooks/useIsLoggedIn';

type IDeclineReviewFormData = {
  declineReason: string;
  declineReasonList: string;
  recommendReviewers: boolean;
  recommendedReviewers: { email: string }[];
};

const LinkInvalid = () => {
  return (
    <Layout width="960px">
      <Flex flexDirection="column" gap={8}>
        <Flex
          height="112px"
          width="100%"
          background="errorPrimaryFill"
          justifyContent="center"
          alignItems="center"
          borderRadius="base"
          color="actionPrimaryCautionBorderHover"
        >
          <FontAwesomeIcon fontSize={40} icon={faTriangleExclamation} />
        </Flex>
        <Flex flexDirection="column" gap={4} textAlign="center">
          <Text textStyle="headingTitle">Invalid/Expired URL</Text>
          <Text textStyle="textBody">Provided url is invalid or expired.</Text>
        </Flex>
      </Flex>
    </Layout>
  );
};

const InformationSentToEditor = ({
  navigateAway
}: {
  navigateAway: () => void;
}) => {
  const isLoggedIn = useIsLoggedIn();

  return (
    <Layout width="960px">
      <Flex flexDirection="column" gap={8}>
        <Flex
          height="112px"
          width="100%"
          background="neutralSecondaryFill"
          justifyContent="center"
          alignItems="center"
          borderRadius="base"
          color="neutralSecondaryContent"
        >
          <FontAwesomeIcon fontSize={40} icon={faCheckCircle} />
        </Flex>
        <Flex
          flexDirection="column"
          gap={4}
          textAlign="center"
          alignItems="center"
        >
          <Text textStyle="headingTitle">Information Sent</Text>
          <Text textStyle="textBody">
            You've sent your information to the reviewer who invited you to
            participate in this Co-Review. We appreciate your time and expertise
            in considering this review.
          </Text>
          <Button variant="primary" onClick={navigateAway}>
            {isLoggedIn ? 'View Active Reviews' : 'Log In'}
          </Button>
        </Flex>
      </Flex>
    </Layout>
  );
};

const DeclineCoReview = () => {
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const [isFormSubmitted, setIsFormSubmitted] = useState<boolean>(false);
  const methods = useForm<IDeclineReviewFormData>({
    defaultValues: {
      declineReason: '',
      recommendReviewers: false,
      recommendedReviewers: [{ email: '' }]
    }
  });
  const isLoggedIn = useIsLoggedIn();

  const {
    formState: { errors },
    watch,
    register,
    setError,
    handleSubmit
  } = methods;

  if (!id) {
    return <LinkInvalid />;
  }

  const declineCoReviewReason = updateCoReviewDeclineReason(id);

  const onSubmit = async (data: IDeclineReviewFormData) => {
    if (!data.declineReason) {
      setError(
        'declineReason',
        { message: 'Please enter your reason for declining.' },
        {
          shouldFocus: true
        }
      );
      return;
    }

    await declineCoReviewReason.mutateAsync({
      declineReason: data.declineReason
    });

    setIsFormSubmitted(true);
  };

  const navigateAway = () => {
    if (isLoggedIn) {
      navigate('/app/reviews');
    } else {
      // TODO:
      // We don't have an explicit login route,
      // so this will trigger the login flow and lead them to the dashboard after signing in
      navigate('/app/home');
    }
  };

  useConfirmNavigation(
    'Are you sure you want to go back? Changes may not be saved.',
    methods.formState.isDirty && !isFormSubmitted
  );

  return (
    <>
      {isFormSubmitted ? (
        <InformationSentToEditor navigateAway={navigateAway} />
      ) : (
        <FormProvider {...methods}>
          <form onSubmit={handleSubmit(onSubmit)}>
            <Flex flexDirection="column">
              <Flex flexDirection="column" gap={16}>
                <Layout width="960px">
                  <Flex flexDirection="column" gap={8}>
                    <Flex
                      height="112px"
                      width="100%"
                      background="neutralSecondaryFill"
                      justifyContent="center"
                      alignItems="center"
                      borderRadius="base"
                      color="neutralSecondaryContent"
                    >
                      <FontAwesomeIcon fontSize={40} icon={faFileXmark} />
                    </Flex>
                    <Flex flexDirection="column" gap={4}>
                      <Text textStyle="headingTitle">Co-Review Declined</Text>
                      <Text textStyle="textBody">
                        Your decision has been recorded, and we appreciate your
                        time and expertise in considering this review. Before
                        closing, you can also provide a reason for this
                        decision, or suggest other reviewers who might be a good
                        fit.
                      </Text>
                    </Flex>
                    <Flex flexDirection="column" gap={4}>
                      <Flex flexDirection="column">
                        <Text textStyle="headingLine">
                          Reason for Declining
                        </Text>
                        <Text textStyle="textBody">
                          Providing a reason helps us improve future reviewer
                          selections.
                        </Text>
                      </Flex>
                      <TextArea
                        placeholder="Provide a reason"
                        value={watch('declineReason')}
                        {...register('declineReason')}
                        isInvalid={Boolean(errors.declineReason)}
                        errorMessage={errors.declineReason?.message}
                      />
                    </Flex>
                  </Flex>
                </Layout>
              </Flex>
              <Flex
                paddingY={4}
                paddingX={12}
                bottom={0}
                zIndex={1}
                justifyContent="flex-end"
                position="sticky"
                bgColor="surfacePrimaryFill"
                marginX="auto"
                width="100%"
                gap={2}
              >
                <Button variant="secondary" onClick={navigateAway}>
                  {isLoggedIn ? 'Back to Active Reviews' : 'Log In'}
                </Button>
                <Button variant="primary" type="submit">
                  Submit
                </Button>
              </Flex>
            </Flex>
          </form>
        </FormProvider>
      )}
    </>
  );
};

export default DeclineCoReview;
